import { Box, Flex, Group, Text } from '@mantine/core';
import { IconButton } from 'src/design-system/IconButton';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Client } from 'src/admin/users/types';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { CreateFamilyPayload, useCreateFamily } from '../hooks/useCreateFamily';
import { useUpdateFamily } from '../hooks/useUpdateFamily';
import { useUpdateClient } from '../hooks/useUpdateClient';
import CreateClientModal from '../CreateClientModalV2';
import {
  roleModelPayloadFromInProgressFamilyV2,
  starModelPayloadFromInProgressFamilyV2,
  V2MemberRelationship,
  V2MemberRoles,
} from './dataHelpers';
import MemberSearchList from './MemberSearchList';
import { SearchbarActionButton } from 'src/design-system/SearchbarActionButton';
import NameAndRelationshipsStep from './NameAndRelationshipsStep';
import NameAndRolesStep from './NameAndRolesStep';
import ConfirmReassignAdvisorModal from '../CreateFamily/ConfirmReassignAdvisorModal';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { CreateFamilySidebar } from './CreateFamilySidebar';

type FamilyFlowProps = {
  mode: 'create' | 'edit';
  familyId?: string;
  initialMembers?: Client[];
  initialName?: string;
  initialRoles?: V2MemberRoles;
  initialRelationships?: Record<string, V2MemberRelationship>;
  initialAdvisorUserId?: string;
  editIntent?: 'roles' | 'name';
};

export function FamilyFlow({
  mode,
  familyId,
  initialMembers = [],
  initialName = '',
  initialRoles = {},
  initialRelationships,
  initialAdvisorUserId,
  editIntent = 'roles',
}: FamilyFlowProps) {
  const navigate = useNavigate();
  const { showConfirmation } = useGlobalConfirmation();
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { flagEnabledFamilyRoleModel } = useFlags();
  const { createFamily, isLoading: isCreating } = useCreateFamily();
  const { updateFamily, isLoading: isUpdating } = useUpdateFamily();
  const { updateClient, isLoading: isReassigning } = useUpdateClient();
  // Editing starts on the details screen (name + roles/relationships); member
  // selection is reached by stepping back. Creating starts on member selection.
  const [step, setStep] = useState<'select' | 'details'>(mode === 'edit' ? 'details' : 'select');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Client[]>(initialMembers);
  const [showAddIndividual, setShowAddIndividual] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState<string | null>(
    initialAdvisorUserId ?? null,
  );
  const [pendingPayload, setPendingPayload] = useState<CreateFamilyPayload | null>(null);

  const isEdit = mode === 'edit';
  const isSaving = isCreating || isUpdating;
  const actionLabel = isEdit ? 'Save changes' : 'Create family';
  const isNameChange = isEdit && editIntent === 'name';
  const detailsEditable = !isNameChange;
  const cancelPath = isEdit && familyId ? `/family/${familyId}/profile` : '/clients/families';
  // Editing name/roles starts on the details screen, so "back" returns to the
  // family profile. Creating reaches details from member selection, so "back"
  // steps back to that selection.
  const detailsBack = isEdit ? () => navigate(cancelPath) : () => setStep('select');
  const relationshipsTitle = isNameChange ? 'Change family name' : undefined;
  const rolesTitle = isEdit && !isNameChange ? 'Manage roles' : relationshipsTitle;
  const advisorUserId = selectedAdvisor ?? currentUser?.id ?? '';
  const canChooseAdvisor = isSystemAdmin || isServiceAdmin;
  const conflictingMembers = selectedMembers.filter(
    m => m.advisorUserId && m.advisorUserId !== advisorUserId,
  );

  const toggleMember = (client: Client) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === client.id);
      if (isSelected) return prev.filter(m => m.id !== client.id);
      return [...prev, client];
    });
  };

  const handleNewClientAdded = (client: Client) => {
    setSelectedMembers(prev => [...prev, client]);
    setShowAddIndividual(false);
  };

  const persistFamily = async (payload: CreateFamilyPayload) => {
    if (isEdit && familyId) {
      return updateFamily(familyId, payload);
    }
    return createFamily(payload);
  };

  const submitFamily = async (payload: CreateFamilyPayload) => {
    try {
      const result = await persistFamily(payload);
      Mixpanel.track(isEdit ? MixpanelEvents.FAMILY_UPDATED : MixpanelEvents.FAMILY_CREATED);
      if (isEdit) {
        showConfirmation(isNameChange ? 'Family name changed' : 'Successfully updated');
      }
      const targetFamilyId = isEdit ? familyId : result?.data?.id;
      navigate(targetFamilyId ? `/family/${targetFamilyId}/profile` : '/clients/families');
    } catch (error) {
      const message = ApiError.isApiError(ApiErrorCode.CLIENT_ALREADY_IN_FAMILY, error)
        ? error.message
        : `An error occurred while ${isEdit ? 'updating' : 'creating'} the family. Please try again.`;
      notifyAndLogError(`Failed to ${isEdit ? 'update' : 'create'} family`, message, error);
    }
  };

  // Creating a family requires every member to belong to the family's advisor.
  // When an admin picks a different advisor, reassign the conflicting members
  // first (with confirmation), mirroring the legacy flow.
  const attemptSubmit = async (payload: CreateFamilyPayload) => {
    if (conflictingMembers.length > 0) {
      setPendingPayload(payload);
      return;
    }
    await submitFamily(payload);
  };

  const handleSubmit = (familyName: string, relationships: Record<string, V2MemberRelationship>) =>
    attemptSubmit(
      starModelPayloadFromInProgressFamilyV2(
        { selectedMembers, relationships, name: familyName },
        advisorUserId,
      ),
    );

  const handleSubmitRoles = (familyName: string, roles: V2MemberRoles) =>
    attemptSubmit(
      roleModelPayloadFromInProgressFamilyV2(
        { selectedMembers, roles, name: familyName },
        advisorUserId,
      ),
    );

  const handleConfirmReassign = async () => {
    try {
      await Promise.all(
        conflictingMembers.map(member => updateClient(member.id, { advisorUserId })),
      );
    } catch (error) {
      notifyAndLogError(
        'Failed to reassign clients',
        'An error occurred while reassigning clients. Please try again.',
        error,
      );
      return;
    }
    const payload = pendingPayload;
    setPendingPayload(null);
    if (payload) await submitFamily(payload);
  };

  if (step === 'details') {
    return (
      <>
        {flagEnabledFamilyRoleModel ? (
          <NameAndRolesStep
            selectedMembers={selectedMembers}
            onBack={detailsBack}
            onCancel={() => navigate(cancelPath)}
            onSubmit={handleSubmitRoles}
            isLoading={isSaving}
            showAdvisorPicker={canChooseAdvisor}
            advisorUserId={advisorUserId}
            onAdvisorChange={setSelectedAdvisor}
            initialFamilyName={initialName}
            initialRoles={initialRoles}
            actionLabel={actionLabel}
            title={rolesTitle}
            rolesEditable={detailsEditable}
          />
        ) : (
          <NameAndRelationshipsStep
            selectedMembers={selectedMembers}
            onBack={detailsBack}
            onCancel={() => navigate(cancelPath)}
            onSubmit={handleSubmit}
            isLoading={isSaving}
            showAdvisorPicker={canChooseAdvisor}
            advisorUserId={advisorUserId}
            onAdvisorChange={setSelectedAdvisor}
            initialFamilyName={initialName}
            initialRelationships={initialRelationships}
            actionLabel={actionLabel}
            title={relationshipsTitle}
            relationshipsEditable={detailsEditable}
          />
        )}
        <SidebarContent>
          <SiestaBarLayout>
            <CreateFamilySidebar step="details" />
          </SiestaBarLayout>
        </SidebarContent>
        <CreateClientModal
          opened={showAddIndividual}
          close={() => setShowAddIndividual(false)}
          navigateAfterCreation={false}
          onClientCreated={handleNewClientAdded}
        />
        {pendingPayload && (
          <ConfirmReassignAdvisorModal
            conflictingMembers={conflictingMembers}
            targetAdvisorUserId={advisorUserId}
            isLoading={isReassigning || isSaving}
            onConfirm={handleConfirmReassign}
            onCancel={() => setPendingPayload(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Flex direction="column" h="100%">
        <FlowLayout
          header={
            <Box px="xl" pt="xxl" pb="sm">
              <Group justify="space-between" pb="lg">
                <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                  {isEdit ? 'Edit family members' : 'Select family members'}
                </Text>
                <DesignButton variant="text" onClick={() => navigate(cancelPath)}>
                  Cancel
                </DesignButton>
              </Group>

              <Group pb="md" gap="sm">
                <IconButton
                  size={48}
                  bg="brand.0"
                  c="brand.7"
                  style={{ flexShrink: 0 }}
                  onClick={() => {
                    if (searchOpen) setSearchTerm('');
                    setSearchOpen(o => !o);
                  }}
                >
                  {searchOpen ? <IconX size={24} /> : <IconSearch size={24} />}
                </IconButton>

                <SearchbarActionButton
                  searchOpen={searchOpen}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, email, address"
                  buttonLabel="Add new individual"
                  onButtonClick={() => setShowAddIndividual(true)}
                />
              </Group>
            </Box>
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <FlowNavigationFooter
                  onBack={() => navigate(-1)}
                  onAction={() => setStep('details')}
                  actionDisabled={selectedMembers.length < 2}
                  actionDisabledTooltip="Select at least 2 members to create a family"
                />
              }
            >
              <Box px="xs">
                <MemberSearchList
                  selectedMembers={selectedMembers}
                  onToggle={toggleMember}
                  searchTerm={searchTerm}
                  noScroll
                  excludeInFamilies
                />
              </Box>
            </FlowBody>
          }
        />
      </Flex>
      <SidebarContent>
        <SiestaBarLayout>
          <CreateFamilySidebar step="select" />
        </SiestaBarLayout>
      </SidebarContent>
      <CreateClientModal
        opened={showAddIndividual}
        close={() => setShowAddIndividual(false)}
        navigateAfterCreation={false}
        onClientCreated={handleNewClientAdded}
      />
    </>
  );
}

export default function CreateFamilyPage() {
  const location = useLocation();
  const initialMembers = (location.state as { initialMembers?: Client[] } | null)?.initialMembers;
  return <FamilyFlow mode="create" initialMembers={initialMembers} />;
}
